/**
 *
 *  Organizations/Show/AdditionalQuestions/ResponsesSelector
 *
 */

import React, { useState, useCallback, useRef } from 'react'

// NPM Libraries
import PropTypes from 'prop-types'
import Toggle from 'react-toggle'
import update from 'immutability-helper'
import styled from '@emotion/styled'
import { useDrag, useDrop, DndProvider } from 'react-dnd'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { HTML5Backend } from 'react-dnd-html5-backend'

// Utils
import { COLORS } from 'global-styles'

const ToggleContainer = styled.div`
	display: flex;

	.react-toggle {
		transform: scale(0.75);
	}
`

const List = styled.div`
	border: 1px solid ${COLORS.grey_e};
	max-height: 200px;
	overflow-x: auto;

	.item:last-child {
		border-bottom: none;
	}
`

const ListItem = styled.div`
	padding: 0 10px;
	display: flex;
	align-items: center;
	border-bottom: 1px solid ${COLORS.grey_e};

	&:focus-within {
		border-color: ${COLORS.primary};

		.drag-icon {
			color: ${COLORS.primary};
		}

		.delete-icon {
			visibility: visible;
		}
	}

	input {
		border: 0 !important;
	}

	.drag-icon {
		color: ${COLORS.grey_e};
		cursor: move;
	}

	.delete-icon {
		color: ${COLORS.danger};
		visibility: hidden;
		cursor: pointer;
	}

	&:hover {
		.drag-icon {
			color: ${COLORS.primary};
		}
		.delete-icon {
			visibility: visible;
		}
	}
`

function DragAndDropItem({ index, response, handleMove, handleUpdate, handleDelete }) {
	const ref = useRef(null)

	const [{ handlerId }, drop] = useDrop({
		accept: 'response',
		collect(monitor) {
			return {
				handlerId: monitor.getHandlerId(),
			}
		},
		hover(item, monitor) {
			if (!ref.current) {
				return
			}
			const dragIndex = item.index
			const hoverIndex = index
			// Don't replace items with themselves
			if (dragIndex === hoverIndex) {
				return
			}
			const hoverBoundingRect = ref.current?.getBoundingClientRect()
			const hoverMiddleY = (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2
			const clientOffset = monitor.getClientOffset()

			const hoverClientY = clientOffset.y - hoverBoundingRect.top
			// Dragging downwards
			if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
				return
			}
			// Dragging upwards
			if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
				return
			}
			// Time to actually perform the action
			handleMove(dragIndex, hoverIndex)

			// eslint-disable-next-line
			item.index = hoverIndex
		},
	})

	const [{ isDragging }, drag] = useDrag({
		item: { type: 'response', id: index, index },
		collect: (monitor) => ({
			isDragging: monitor.isDragging(),
		}),
	})

	drag(drop(ref))

	return (
		<ListItem
			ref={ref}
			data-handler-id={handlerId}
			className="item"
			style={{
				opacity: isDragging ? 0.5 : 1,
			}}
		>
			<FontAwesomeIcon icon="grip-vertical" size="md" className="drag-icon" />

			<input
				value={response}
				className="form-control"
				name={`campaing_default_response_${index}`}
				onChange={(e) => handleUpdate(e.target.value, index)}
				onBlur={(e) => {
					if (e.target.value === '') handleDelete(index)
				}}
			/>

			<FontAwesomeIcon icon="times" className="delete-icon" onClick={() => handleDelete(index)} />
		</ListItem>
	)
}

function ResponsesSelector({ question, onEditQuestion, error }) {
	const { options: responses, freeform_response } = question

	const ref = useRef(null)

	const [new_response, setNewResponse] = useState('')

	const handleMoveResponses = useCallback(
		(dragIndex, hoverIndex) => {
			const dragResponse = responses[dragIndex]

			onEditQuestion(
				update(responses, {
					$splice: [
						[dragIndex, 1],
						[hoverIndex, 0, dragResponse],
					],
				}),
			)
		},
		[responses, onEditQuestion],
	)

	const handleUpdateResponses = (value, index) => {
		onEditQuestion(responses.map((ele, i) => (i === index ? value : ele)))
	}

	const handleRemoveResponses = (index) => {
		onEditQuestion(
			responses.reduce((acc, ele, i) => {
				if (i !== index) {
					acc.push(ele)
				}

				return acc
			}, []),
		)
	}

	return (
		<div style={{ marginLeft: '20px' }}>
			<ToggleContainer>
				<Toggle
					value={freeform_response}
					checked={freeform_response}
					onChange={() => onEditQuestion([], !freeform_response)}
				/>
				<span>Response as a Free form</span>
			</ToggleContainer>

			{!freeform_response && (
				<div className="mt-2">
					<List>
						<DndProvider backend={HTML5Backend}>
							{(responses || []).map((response, index) => (
								<DragAndDropItem
									key={index}
									index={index}
									response={response}
									handleMove={handleMoveResponses}
									handleUpdate={handleUpdateResponses}
									handleDelete={handleRemoveResponses}
								/>
							))}
						</DndProvider>
					</List>

					<div className="d-flex mt-3">
						<input
							ref={ref}
							value={new_response || ''}
							name="new_response"
							className={`form-control ${error ? 'error' : ''}`}
							placeholder="Type New Response"
							onChange={(e) => {
								setNewResponse(e.target.value)
							}}
							onKeyDown={(e) => {
								if (e.key === 'Enter' && e.target.value !== '') {
									onEditQuestion([...responses, new_response])
									setNewResponse('')
								}
							}}
							autoFocus
						/>

						<button
							className="btn btn-primary"
							style={{ width: '60px' }}
							onClick={() => {
								if (new_response === '') return

								onEditQuestion([...responses, new_response])
								setNewResponse('')

								ref.current.focus()
							}}
						>
							Add
						</button>
					</div>
				</div>
			)}
		</div>
	)
}

ResponsesSelector.propTypes = {
	question: PropTypes.object,
	onEditQuestion: PropTypes.func,
	error: PropTypes.bool,
}

DragAndDropItem.propTypes = {
	index: PropTypes.number,
	response: PropTypes.string,
	handleMove: PropTypes.func,
	handleUpdate: PropTypes.func,
	handleDelete: PropTypes.func,
}

export default ResponsesSelector
